import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { getDeviceId } from '@/utils/deviceId';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const DEFAULT_TIMEOUT_MS = 60000;
const REFRESH_TIMEOUT_MS = 30000;

const client = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
  timeout: DEFAULT_TIMEOUT_MS,
});

// Shared refresh promise so concurrent 401s do not race each other and
// consume the same refresh token more than once.
let refreshPromise: Promise<string> | null = null;

interface ApiErrorBody {
  code: string;
  message: string;
}

const REQUEST_TIMEOUT_ERROR: ApiErrorBody = {
  code: 'REQUEST_TIMEOUT',
  message: '请求超时，请稍后重试',
};

const NETWORK_ERROR: ApiErrorBody = {
  code: 'NETWORK_ERROR',
  message: '网络异常，请检查网络连接',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Maps transport failures (timeout / no response) to the app's standard error
 * envelope; HTTP errors and non-object throws pass through unchanged.
 */
export function toApiError(error: unknown): unknown {
  if (!isRecord(error)) {
    return error;
  }

  const code = typeof error.code === 'string' ? error.code : '';
  const message = typeof error.message === 'string' ? error.message : '';
  const isTimeout = code === 'ECONNABORTED' || message.toLowerCase().includes('timeout');

  if (!isTimeout && error.response) {
    return error;
  }

  const body = isTimeout ? REQUEST_TIMEOUT_ERROR : NETWORK_ERROR;
  return Object.assign(new Error(body.message), error, {
    message: body.message,
    response: { data: { error: body }, status: 0 },
  });
}

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const deviceId = getDeviceId();
  if (deviceId) {
    config.headers['X-Device-Id'] = deviceId;
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && !error.config._retry) {
      error.config._retry = true;

      if (!refreshPromise) {
        refreshPromise = (async () => {
          try {
            const refreshToken = localStorage.getItem('auth_refresh_token');
            const res = await axios.post(
              `${API_BASE_URL}/api/v1/auth/refresh`,
              { refresh_token: refreshToken },
              { timeout: REFRESH_TIMEOUT_MS }
            );
            const { access_token, refresh_token } = res.data.data;

            localStorage.setItem('auth_access_token', access_token);
            if (refresh_token) {
              localStorage.setItem('auth_refresh_token', refresh_token);
            }
            useAuthStore.setState({
              accessToken: access_token,
              refreshToken: refresh_token,
            });

            return access_token;
          } catch (err) {
            localStorage.removeItem('auth_access_token');
            localStorage.removeItem('auth_refresh_token');
            useAuthStore.setState({
              accessToken: null,
              refreshToken: null,
              user: null,
              isAuthenticated: false,
            });
            window.location.href = '/login';
            throw err;
          } finally {
            refreshPromise = null;
          }
        })();
      }

      try {
        const access_token = await refreshPromise;
        error.config.headers.Authorization = `Bearer ${access_token}`;
        return client(error.config);
      } catch (err) {
        return Promise.reject(err);
      }
    }
    return Promise.reject(toApiError(error));
  }
);

export default client;
